(ns dre.widgets.burden-of-disease.events
  (:require
   [dre.fx :as-alias dre.fx]
   [clojure.string :as str]
   [dre.app.countries :as countries]
   [dre.i18n.utils :as i18n-utils]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.network :as-alias network]
   [dre.widgets.demographics.events :as demographics]
   [dre.widgets.geography.events :as geography]
   [dre.widgets.dates.events :as dates]
   [dre.utils.query :as query]
   [dre.utils :refer [stringify-keys-with-namespaces aborted-request?]]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))


(def db-path [:widgets :burden-of-disease])

(def id :burden-of-disease)

;; -- Utils

(def ^:private newlines
  {:lf "\n" :cr+lf "\r\n"})

(def ^:private newline-error-message
  (str ":newline must be one of [" (str/join "," (keys newlines)) "]"))

(defn- escape-quotes [s]
  (str/replace s "\"" "\"\""))

(defn- wrap-in-quotes [s]
  (str "\"" (escape-quotes s) "\""))

(defn- needs-quote? [s separator newline]
  (or (str/includes? s separator)
      (str/includes? s newline)
      (str/includes? s "\"")))

(defn- auto-quote [s separator newline]
  (if (needs-quote? s separator newline)
    (wrap-in-quotes s)
    s))

(defn- separate [data separator newline quote?]
  (str/join separator
            (cond->> data
              :always (map str)
              quote? (map wrap-in-quotes)
              (not quote?) (map #(auto-quote % separator newline)))))

(defn- write-data [data separator newline quote?]
  (str/join newline (map #(separate % separator newline quote?) data)))

(defn write-csv
  "Writes data to String in CSV-format.
  Accepts the following options:
  :separator - field separator
               (default ,)
  :newline   - line separator
               (accepts :lf or :cr+lf)
               (default :lf)
  :quote?    - wrap in quotes
               (default false)"

  {:arglists '([data] [data & options]) :added "0.1.0"}
  [data & options]
  (let [{:keys [separator newline quote?] :or {separator "," newline :lf quote? false}} options]
    (if-let [newline-char (get newlines newline)]
      (write-data data
                  separator
                  newline-char
                  quote?)
      (throw (js/Error. newline-error-message)))))


(defn- pill->senses
  [{:pill/keys [query other-senses]}]
  (concat [query] other-senses))

(defn- query->senses
  [{:query/keys [pills]}]
  (->> pills
       (mapcat pill->senses)
       (keep identity)))

(defn- nested-group-by-sense-location
  [response]
  (->> response
       (group-by :cause)
       (map (fn [[k v]]
              [k (->> v
                      (group-by :location)
                      (map (fn [[k v]] [k v]))
                      (into {}))]))
       (into {})))

(defn- combine-country-codes
  "Combines country codes from results of different senses.

   raw-result's structure: {<some sense> {<country 1> <results>
                                          <country 2> <results>}
                            <some other sense> {<country a> <results>
                                                <country b> <results>}}"
  [raw-results]
  (->> raw-results
       vals
       (mapcat keys)
       distinct))

(defn- ->hash-map
  "Generates a hash-map with below structure:
   {:country <country-name>
    <disease-1> {\"Both\" <total-count>
                 \"Female\" <female-count>
                 \"Male\" <male-count>}
   <disease-2> ...
   etc.}"
  [raw-results diseases country-code]
  (reduce (fn [acc disease]
            (let [results (get-in raw-results [disease country-code])
                  female-count (->> results
                                    (filter #(= (:gender %)
                                                "female"))
                                    first
                                    :value)
                  male-count (->> results
                                  (filter #(= (:gender %)
                                              "male"))
                                  first
                                  :value)
                  total-count (+ female-count male-count)]
              (assoc acc disease {"Both" total-count
                                  "Female" female-count
                                  "Male" male-count})))
          {:country (if (= "GLOBAL" country-code)
                      "Global"
                      (countries/code->name country-code))}
          diseases))

(defn- ->rows-for-country
  "Generates 3 rows for the given country. Each row represents different genders
   (female, male, both) and contains counts for every disease.

   If there are 2 diseases the output will be something like below:
   [[\"USA\" \"Both\"   100 90]
    [\"USA\" \"Female\" 60 40]
    [\"USA\" \"Male\"   40 50]]"
  [diseases {:keys [country] :as data}]
  (map (fn [gender]
         (concat [country gender]
                 (map #(get-in data [% gender]) diseases)))
       ["Both" "Female" "Male"]))

(defn- ->csv-rows
  "Gets all country codes from all disease results, generate 3 tuples for each
   country for all genders(both, female, male) that represents csv rows,
   excluding header row. Rows of same countries are grouped together regardless
   of counts. Countries are sorted by the first disease's total count."
  [raw-results diseases]
  (let [country-codes (combine-country-codes raw-results)]
    (->> country-codes
         (map #(->hash-map raw-results diseases %))
         (sort-by #(get-in % [(first diseases) "Both"]) >)
         (mapcat #(->rows-for-country diseases %)))))

(defn- selected-genders
  [db]
  (let [genders (->> (concat (demographics/gender-filters db) (demographics/gender-queries db))
                     (map :pill/label)
                     (map str/lower-case)
                     set)]
    (if (empty? genders)
      #{"male" "female"}
      genders)))

(defn selected-countries
  [db]
  (let [x (->> (concat (geography/country-filters db) (geography/country-queries db))
               (map :pill/label)
               (keep countries/name->code))
        y (->> (concat (geography/country-filters db) (geography/country-queries db))
               (keep :pill/country-code))]
    (set (concat x y))))

(defn- date-string->year
  [s]
  (-> s
      (str/split "-")
      first
      js/parseInt))

(defn- year-to-show
  [db]
  (if-let [year (->> (concat (dates/published-through-filters db) (dates/published-through-queries db))
                     (map :pill/end)
                     (map date-string->year)
                     (apply max))]
    year
    2019))


;; -- Events

(def  IHME-MEASURE-ID {:prevalence 5 :deaths 1 :incidence 6})

(rf/reg-event-fx
 ::set-loading
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  {:db (assoc db :state :loading)}))

(rf/reg-event-fx
 ::init
 [(rf/path db-path) rf/trim-v]
 (fn
   [{db :db} [{:keys [after-fx search-query]}]]
   (if-let [senses (query->senses search-query)]
     {:db {:state :loading}
      :dispatch-n [after-fx
                   [::incidence senses]
                   [::prevalence senses]
                   [::deaths senses]]}
     {:db (assoc db :state nil)
      :dispatch after-fx})))

(rf/reg-event-fx
 ::incidence
 [rf/trim-v]
 (fn
   [{db :db} [senses]]
   (let [params
         {:senses senses
          :genders (selected-genders db)
          :locations (if (seq (selected-countries db))
                       (selected-countries db)
                       ["GLOBAL" "ALL_COUNTRIES"])
          :ages ["All Ages"]
          :measure_id (:incidence IHME-MEASURE-ID)
          :years [(year-to-show db)]}]
     {::network/request-with-abort [{:endpoint :widgets/burden-of-disease-incidence}
                                    {:body (stringify-keys-with-namespaces params)
                                     :on-success [::success-incidence]
                                     :on-failure [::failure]}]})))

(rf/reg-event-fx
 ::success-incidence
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  (let [r (:body response)
        senses (distinct (map :cause r))
        results (nested-group-by-sense-location r)]
    {:db {:state :success
          :data {:results (merge (get-in db [:data :results]) {:incidence results})
                 :senses (distinct (concat (get-in db [:data :senses]) senses))}}})))

(rf/reg-event-fx
 ::prevalence
 [rf/trim-v]
 (fn
   [{db :db} [senses]]
   (let [params
         {:senses senses
          :genders (selected-genders db)
          :locations (if (seq (selected-countries db))
                       (selected-countries db)
                       ["GLOBAL" "ALL_COUNTRIES"])
          :ages ["All Ages"]
          :measure_id (:prevalence IHME-MEASURE-ID)
          :years [(year-to-show db)]}]
     {::network/request-with-abort [{:endpoint :widgets/burden-of-disease-prevalence}
                                    {:body (stringify-keys-with-namespaces params)
                                     :on-success [::success-prevalence]
                                     :on-failure [::failure]}]})))

(rf/reg-event-fx
 ::success-prevalence
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  (let [r (:body response)
        senses (distinct (map :cause r))
        results (nested-group-by-sense-location r)]
    {:db {:state :success
          :data {:results (merge (get-in db [:data :results]) {:prevalence results})
                 :senses (distinct (concat (get-in db [:data :senses]) senses))}}})))

(rf/reg-event-fx
 ::deaths
 [rf/trim-v]
 (fn
   [{db :db} [senses]]
   (let [params
         {:senses senses
          :genders (selected-genders db)
          :locations (if (seq (selected-countries db))
                       (selected-countries db)
                       ["GLOBAL" "ALL_COUNTRIES"])
          :ages ["All Ages"]
          :measure_id (:deaths IHME-MEASURE-ID)
          :years [(year-to-show db)]}]
     {::network/request-with-abort [{:endpoint :widgets/burden-of-disease-deaths}
                                    {:body (stringify-keys-with-namespaces params)
                                     :on-success [::success-deaths]
                                     :on-failure [::failure]}]})))

(rf/reg-event-fx
 ::success-deaths
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [::fetch-fx/response]}]]
  (let [r (:body response)
        senses (distinct (map :cause r))
        results (nested-group-by-sense-location r)]
    {:db {:state :success
          :data {:results (merge (get-in db [:data :results]) {:deaths results})
                 :senses (distinct (concat (get-in db [:data :senses]) senses))}}})))

(rf/reg-event-fx
 ::failure
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [_ [{:keys [::fetch-fx/response] :as opts}]]
  (if (aborted-request? response)
    {}
    {:db {:state :failure
          :data nil}
     :dispatch-n [[:dre.network/default-failure (merge {:event :burden-of-disease} opts)]]})))

(rf/reg-event-fx
 ::export
 [rf/trim-v]
 (fn-traced
  [{root-db :db} [ihme-key]]
  (let [db (get-in root-db db-path)
        i18n-db (:i18n root-db)
        diseases (get-in db [:data :senses])
        results (get (get-in db [:data :results]) ihme-key)
        year (year-to-show db)
        csv (write-csv (cons (concat [(str (i18n-utils/translate i18n-db "Year, ") year) (i18n-utils/translate i18n-db "Gender")] diseases)
                             (->csv-rows results diseases))
                       :quote? true)]
    (rf/dispatch [::dre.fx/download {:content csv
                                     :filename (str ihme-key ".csv")
                                     :mimetype "text/csv"}])
    nil)))


;; -- Subscriptions

(rf/reg-sub
 ::state
 (fn [db _]
   (get-in db db-path)))

(rf/reg-sub
 ::data-senses
 :<- [::state]
 (fn [state _]
   (let [data (get-in state [:data :senses] nil)]
     data)))

(rf/reg-sub
 ::data
 :<- [::state]
 (fn [state [selected-type]]
   (let [data (get-in state [:data :results selected-type] nil)]
     data)))

(rf/reg-sub
 ::selected-genders
 selected-genders)

(rf/reg-sub
 ::selected-countries
 selected-countries)

(rf/reg-sub
 ::year-to-show
 year-to-show)

(rf/reg-sub
 ::data-map
 :<- [::state]
 :<- [::selected-genders]
 (fn [[state genders] [_ selected-type selected-sense]]
   (let [data (get-in state [:data :results selected-type] nil)]
     (->> selected-sense
          (get data)
          (map (fn [[k values]]
                 (let [count (->> values ; calcualte sum of selected genders
                                  (filter #(contains? genders (:gender %)))
                                  (map :value)
                                  (reduce +))]
                   [k {:val count}])))
          (into {})))))

(rf/reg-sub
 ::data-list
 :<- [::state]
 :<- [::selected-genders]
 (fn [[state genders] [_ selected-type selected-sense]]
   (let [data (get-in state [:data :results selected-type] nil)]
     (->> selected-sense
          (get data)
          (map (fn [[country-code values]]
                 (let [female (->> values
                                   (filter #(= (:gender %) "female"))
                                   first)
                       male (->> values
                                 (filter #(= (:gender %) "male"))
                                 first)
                       [total-count lower upper]
                       (condp = genders
                         #{"female" "male"} [(+ (:value female) (:value male))
                                             (+ (:lower female) (:lower male))
                                             (+ (:upper female) (:upper male))]
                         #{"female"} [(:value female)
                                      (:lower female)
                                      (:upper female)]
                         #{"male"} [(:value male)
                                    (:lower male)
                                    (:upper male)])]
                   {:term country-code
                    :label (if (= country-code "GLOBAL")
                             "Global"
                             (countries/code->name country-code))
                    :count total-count
                    :lower lower
                    :upper upper
                    :children (when (= genders #{"female" "male"})
                                [{:term :female
                                  :label "Female"
                                  :count (:value female)
                                  :lower (:lower female)
                                  :upper (:upper female)}
                                 {:term :male
                                  :label "Male"
                                  :count (:value male)
                                  :lower (:lower female)
                                  :upper (:upper female)}])})))))))

(rf/reg-sub
 ::status
 :<- [::state]
 (fn [db _]
   (:state db)))
